import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import TimelineNode from 'components/TimelineNode';
import api from 'api';
import { Trans } from 'react-i18next';
import {
  BUYERS_ACTIVITY_TRACK_EVENTS,
  BUYERS_ACTIVITY_TRACK_EVENTS_COPY,
  BuyersActivityTrack,
  BuyersActivityTrackEvents,
} from 'models/Lead';
import { DATA_ROOM_NODE_TAG } from 'router/subrouters/DataRoom/models/DataRoomNode';
import SoftSignalIcon from 'components/SoftSignalIcon';
import DealContext from '../../../../../../providers/Deal/Deal.context';
import { MAX_LIMIT } from 'data/global';
import Nbio from '../Nbio';
import { checkIsUserSuperAdmin, getUserFullName } from 'utils';
import uniqueId from 'lodash/uniqueId';

import './LeadTimeActivity.styles.scss';

type LeadTimeActivityProps = {
  className?: string;
  leadId: string;
};

export type LeadTimeActivityRef = {
  addNewActivity: (activity: Partial<BuyersActivityTrack>) => void;
};

const LeadTimeActivity: React.ForwardRefRenderFunction<
  LeadTimeActivityRef,
  LeadTimeActivityProps
> = (props, ref) => {
  const { className, leadId } = props;

  const [activities, setActivities] = useState<Partial<BuyersActivityTrack>[]>(
    [],
  );

  const { leadStagesCopies } = useContext(DealContext);

  const onNewActivity = useCallback(
    (activity: Partial<BuyersActivityTrack>) => {
      setActivities((prevActivities) => [activity, ...prevActivities]);
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      addNewActivity: onNewActivity,
    }),
    [onNewActivity],
  );

  const classes = classNames('lead-time-activity', className);

  const idForSubmitOffer = useMemo(
    () =>
      activities.find(
        ({ eventType }) =>
          eventType === BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER,
      )?._id,
    [activities],
  );

  const addNewActivity = useCallback(
    (eventType: BuyersActivityTrackEvents) =>
      onNewActivity({
        eventType,
        _id: uniqueId(),
        createdAt: new Date(),
      }),
    [onNewActivity],
  );

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { items },
        } = await api.portfolio.seller.getActivities(leadId, {
          $sort: '-createdAt',
          $limit: MAX_LIMIT,
        });
        setActivities(items);
      } catch {
        return;
      }
    })();
  }, [leadId]);

  if (!activities.length) return null;

  return (
    <div className={classes}>
      {activities.map(
        ({
          _id,
          createdAt,
          eventType,
          dataRoomNodeTag,
          oldStage,
          newStage,
          softSignal,
          dataRoomNode,
          progressedByUser,
        }) => (
          <TimelineNode
            key={_id}
            date={createdAt}
            text={
              <Trans
                i18nKey={BUYERS_ACTIVITY_TRACK_EVENTS_COPY[eventType]}
                values={{
                  oldStage: leadStagesCopies?.[oldStage] || 'Withdraw',
                  newStage: leadStagesCopies?.[newStage] || 'Withdraw',
                  dataRoomNodeName: dataRoomNode?.name,
                  progressedByFullName: checkIsUserSuperAdmin(
                    progressedByUser?.role,
                  )
                    ? 'Resolve team'
                    : getUserFullName(
                        progressedByUser?.firstName,
                        progressedByUser?.lastName,
                      ),
                  tag: DATA_ROOM_NODE_TAG[dataRoomNodeTag],
                }}
                components={{
                  softSignalIcon: <SoftSignalIcon softSignal={softSignal} />,
                }}
              />
            }
          >
            {_id === idForSubmitOffer && (
              <Nbio addNewActivity={addNewActivity} />
            )}
          </TimelineNode>
        ),
      )}
    </div>
  );
};

export default forwardRef(LeadTimeActivity);
